import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, SignaturePad, CURRENT_THERAPIST_AGREEMENT } from '@ah/ui';
import { useAuthStore } from '../auth/auth.store';
import { useAgreementApi } from './useAgreementApi';
import { AgreementApiError } from './agreement.api';

type Phase = 'checking' | 'blocked' | 'allowed' | 'error';

/**
 * Blocking legal gate for the therapist portal (ADR-019).
 *
 * Wraps every authenticated route: until the therapist has signed the CURRENT
 * agreement version, nothing else in the portal renders. A bumped version in
 * @ah/contracts makes `signed` false again server-side, so a new contract
 * re-gates everyone automatically without a client-side version comparison.
 *
 * Signing is deliberately NOT approval — the therapist stays PENDING_REVIEW
 * until an admin vets their licence. The gate says "you accepted our terms",
 * the vetting queue says "we accepted you".
 *
 * Sign-out stays reachable throughout: a legal wall the user cannot escape
 * except by signing would be coercive, and a therapist who disagrees with the
 * contract must be able to walk away.
 */
export function AgreementGate({ children }: { children: React.ReactNode }) {
  const accessToken = useAuthStore((s) => s.tokens?.accessToken);
  const signOut = useAuthStore((s) => s.signOut);
  const api = useAgreementApi();

  const [phase, setPhase] = useState<Phase>('checking');
  const [error, setError] = useState<string | null>(null);
  const [signature, setSignature] = useState<string | null>(null);
  const [accepted, setAccepted] = useState(false);
  const [readToEnd, setReadToEnd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!accessToken) return;
    let cancelled = false;
    setPhase('checking');
    api
      .getStatus(accessToken)
      .then((status) => {
        if (cancelled) return;
        setPhase(status.signed ? 'allowed' : 'blocked');
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        // Fail closed on auth errors (stale token), open on transient ones —
        // a flaky network must not lock a therapist out of their own bookings.
        if (e instanceof AgreementApiError && (e.status === 401 || e.status === 403)) {
          setPhase('blocked');
          setError(e.message);
        } else {
          setPhase('allowed');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [api, accessToken]);

  /** Enables the accept checkbox only once the text has actually been scrolled through. */
  const onScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el || readToEnd) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 24) setReadToEnd(true);
  }, [readToEnd]);

  const submit = useCallback(async () => {
    if (!accessToken || !signature) return;
    setSubmitting(true);
    setError(null);
    try {
      await api.sign(accessToken, {
        agreementVersion: CURRENT_THERAPIST_AGREEMENT.version,
        signatureBase64: signature,
      });
      setPhase('allowed');
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Η υπογραφή απέτυχε. Δοκιμάστε ξανά.');
      setSubmitting(false);
    }
  }, [api, accessToken, signature]);

  if (phase === 'checking') {
    return <div role="status" style={{ padding: 24, textAlign: 'center' }}>Έλεγχος συμφωνητικού…</div>;
  }
  if (phase === 'allowed') return <>{children}</>;

  const canSubmit = readToEnd && accepted && !!signature && !submitting;

  return (
    <main style={wrap}>
      <div style={card}>
        <h1 style={{ margin: '0 0 4px', fontSize: 24 }}>{CURRENT_THERAPIST_AGREEMENT.title}</h1>
        <p style={{ margin: '0 0 var(--ah-space-3)', color: 'var(--ah-color-text-muted)', fontSize: 14 }}>
          Για να συνεχίσετε στην πλατφόρμα, διαβάστε και υπογράψτε το συμφωνητικό συνεργασίας.
          Η υπογραφή σας αποθηκεύεται κρυπτογραφημένα ως νομικό τεκμήριο.
        </p>

        <div
          ref={scrollRef}
          onScroll={onScroll}
          style={scrollBox}
          tabIndex={0}
          aria-label="Κείμενο συμφωνητικού"
          dangerouslySetInnerHTML={{ __html: CURRENT_THERAPIST_AGREEMENT.html }}
        />
        {!readToEnd && (
          <p style={{ fontSize: 13, color: 'var(--ah-color-accent)', margin: '6px 0 0' }}>
            ↓ Κυλήστε μέχρι το τέλος του κειμένου για να συνεχίσετε
          </p>
        )}

        <label style={checkboxRow}>
          <input
            type="checkbox"
            checked={accepted}
            disabled={!readToEnd}
            onChange={(e) => setAccepted(e.target.checked)}
          />
          <span>
            Δηλώνω ότι διάβασα και αποδέχομαι το συμφωνητικό συνεργασίας
            (έκδοση {CURRENT_THERAPIST_AGREEMENT.version}).
          </span>
        </label>

        <div style={{ marginTop: 'var(--ah-space-3)' }}>
          <p style={{ fontWeight: 600, margin: '0 0 6px', fontSize: 14 }}>Ψηφιακή υπογραφή</p>
          <SignaturePad onChange={setSignature} ariaLabel="Περιοχή υπογραφής" />
        </div>

        {error && (
          <p role="alert" style={{ color: 'var(--ah-color-danger, #c62828)', fontSize: 14 }}>
            {error}
          </p>
        )}

        <div style={{ display: 'flex', gap: 'var(--ah-space-3)', marginTop: 'var(--ah-space-3)', flexWrap: 'wrap' }}>
          <Button onClick={submit} disabled={!canSubmit}>
            {submitting ? 'Αποστολή…' : 'Υπογραφή και συνέχεια'}
          </Button>
          <Button variant="secondary" onClick={signOut} disabled={submitting}>
            Αποσύνδεση
          </Button>
        </div>
      </div>
    </main>
  );
}

const wrap: React.CSSProperties = {
  maxWidth: 780,
  margin: '0 auto',
  padding: 'var(--ah-space-4) var(--ah-space-3)',
};

const card: React.CSSProperties = {
  background: 'var(--ah-color-surface)',
  border: '1px solid var(--ah-color-border)',
  borderRadius: 'var(--ah-radius-lg)',
  padding: 'var(--ah-space-4)',
};

const scrollBox: React.CSSProperties = {
  maxHeight: 340,
  overflowY: 'auto',
  border: '1px solid var(--ah-color-border)',
  borderRadius: 'var(--ah-radius-md)',
  padding: 'var(--ah-space-3)',
  background: 'var(--ah-color-bg)',
  fontSize: 14,
  lineHeight: 1.6,
};

const checkboxRow: React.CSSProperties = {
  display: 'flex',
  gap: 10,
  alignItems: 'flex-start',
  marginTop: 'var(--ah-space-3)',
  fontSize: 14,
  cursor: 'pointer',
};
